import { TodoFilter, useWorkQueues, type WorkQueue } from "@nexxt/app-kit";
import type { ListCount } from "@nexxt/contracts";
import { ERP_FILE_QUEUE } from "@nexxt/contracts/integration";
import { STOCK_MISMATCH_RESOLVE } from "@nexxt/contracts/logistics";
import { type WorkQueueId, workQueues } from "@nexxt/contracts/worklist";
import { type LinkOptions, linkOptions } from "@tanstack/react-router";
import { createElement, type ReactNode } from "react";
import { m } from "../../../paraglide/messages.js";
import { useCan } from "../auth/me";
import { homeQueries } from "./home.queries";

// The queues of the user's roles (flussi-per-ruolo §1), "your turn" and waiting on others, read by the home and by
// the sidebar, which adds up the "your turn" ones by place (ADR-114). A queue is the count of a list of its domain
// with fixed filters (ADR-086), all of them asked to worklist in one call (ADR-136): which queues, and in which
// section, the user's permissions choose here. A link opens its list with the filters the queue counts.

export type QueueId = WorkQueueId;

export interface Queue extends WorkQueue {
  /** Also the key of its words in the search (`queuePhrases`), which a state typed in ⌘K opens (ADR-117). */
  id: QueueId;
  /** The place of the sidebar it counts for: its "your turn" count adds up there (ADR-114). */
  place: string;
  /** The list the queue opens, with the same filters it counts. */
  link: LinkOptions;
}

/**
 * The "your turn" queues of a place as one filter "To do" of its list (ADR-302): the rows in one of them at least,
 * which the domain finds with `anyOf`, counted once each by worklist. The sidebar shows `count` and opens `link`.
 */
export interface Todo {
  queues: Queue[];
  /** The number of the sidebar and of the filter: the union of the queues, undefined until counted. */
  count: number | undefined;
  /** The sets of `anyOf` as the list sends them: the filters of each queue, in the order of `queues`. */
  anyOf: string;
  link: LinkOptions;
}

/** The lists with the filter "To do", opened with it on: a place joins with its list, whose domain takes `anyOf`. */
const todoLinks: Partial<Record<string, LinkOptions>> = {
  linesheets: linkOptions({ to: "/linesheets", search: { todo: true } }),
  "purchase-orders": linkOptions({ to: "/purchase-orders", search: { todo: true } }),
  inbounds: linkOptions({ to: "/inbounds", search: { todo: true } }),
  "credit-notes": linkOptions({ to: "/credit-notes", search: { todo: true } }),
  "sales-orders": linkOptions({ to: "/sales-orders", search: { todo: true } }),
  organizations: linkOptions({ to: "/organizations", search: { todo: true } }),
  users: linkOptions({ to: "/admin/users", search: { todo: true } }),
  "stock-adjustments": linkOptions({ to: "/stock-adjustments", search: { todo: true } }),
  fulfillments: linkOptions({ to: "/fulfillments", search: { todo: true } }),
};

type FiltersOf<Q extends WorkQueueId> = (typeof workQueues)[Q] extends { filters: infer F } ? F : never;

/** The filters a queue counts, which its link opens the list with. */
const filtersOf = <Q extends WorkQueueId>(id: Q) => (workQueues[id] as { filters?: unknown }).filters as FiltersOf<Q>;

const linesheets = (id: "to-approve" | "to-price" | "to-recalculate" | "in-draft") =>
  linkOptions({ to: "/linesheets", search: filtersOf(id) });

const attributeTasks = (id: "attributes-to-review" | "attributes-to-fill") =>
  linkOptions({ to: "/attributes", search: filtersOf(id) });

const purchaseOrders = (
  id:
    | "pos-to-approve"
    | "pos-to-publish"
    | "pos-to-decide"
    | "pos-sync-failed"
    | "pos-to-confirm"
    | "pos-reminders-over",
) => linkOptions({ to: "/purchase-orders", search: filtersOf(id) });

const salesOrders = (
  id:
    | "sos-to-allocate"
    | "sos-to-lock"
    | "sos-sync-failed"
    | "sos-fulfillment-stopped"
    | "sos-uncovered"
    | "sos-without-season"
    | "sos-credit-blocked"
    | "sos-awaiting-payment"
    | "sos-deposit-due"
    | "sos-dunning-over"
    | "sos-confirmation-overdue"
    | "sos-confirmation-rejected"
    | "sos-without-confirmation",
) => linkOptions({ to: "/sales-orders", search: filtersOf(id) });

const documents = (id: "documents-to-retry" | "documents-pdf-late") =>
  linkOptions({ to: "/documents", search: filtersOf(id) });

const inbounds = (
  id:
    | "inbounds-to-capture"
    | "inbounds-extraction-to-review"
    | "inbounds-to-check"
    | "inbounds-in-exception"
    | "inbounds-to-authorize"
    | "inbounds-to-dispatch"
    | "inbounds-to-resolve"
    | "inbounds-to-settle"
    | "inbounds-reminders-over",
) => linkOptions({ to: "/inbounds", search: filtersOf(id) });

const creditNotes = (id: "credit-notes-to-review" | "credit-notes-to-send" | "credit-notes-reminders-over") =>
  linkOptions({ to: "/credit-notes", search: filtersOf(id) });

const users = (id: "invitations-reminders-over-customers" | "invitations-reminders-over-partners") =>
  linkOptions({ to: "/admin/users", search: filtersOf(id) });

const stockMovements = (id: "transfers-to-confirm") => linkOptions({ to: "/stock-movements", search: filtersOf(id) });

const stockAdjustments = (id: "adjustments-to-confirm" | "adjustments-to-approve") =>
  linkOptions({ to: "/stock-adjustments", search: filtersOf(id) });

const vendorReturns = (id: "vendor-returns-to-confirm" | "vendor-returns-refused") =>
  linkOptions({ to: "/vendor-returns", search: filtersOf(id) });

const fulfillments = (id: "fulfillments-to-book" | "fulfillments-without-ack") =>
  linkOptions({ to: "/fulfillments", search: filtersOf(id) });

/** A queue before its count: what the user's permissions decide. */
type Shown = Omit<Queue, "data">;

/**
 * A queue that is the turn of who can act on it and, for who only reads it, a wait on them (flussi-per-ruolo §1):
 * written once, and put in the section the user's permissions choose.
 */
const turnOrWait = (queue: Shown, who: { turn: boolean; read: boolean; on: string }) => ({
  turn: who.turn && queue,
  wait: who.read && !who.turn && { ...queue, on: who.on },
});

export function useQueues() {
  const canRead = useCan("linesheet.read");
  const canApprove = useCan("linesheet.approve");
  // FR-M3-03: an approved linesheet waits for Operations to complete its prices
  const canPrice = useCan("pricing.manage") && canRead;
  const canReadAttributes = useCan("attributes.read");
  const canManageAttributes = useCan("attributes.manage") && canReadAttributes;
  // spec v0.2 §4, rule 1: whoever works the integrations sees at once that something is stuck
  const canReadIntegration = useCan("integration.read");
  const canReadErpFiles = useCan(ERP_FILE_QUEUE);

  const canReadPos = useCan("purchase_order.read");
  const canApprovePos = useCan("purchase_order.approve") && canReadPos;
  const canManagePos = useCan("purchase_order.manage") && canReadPos;

  const canReadSos = useCan("sales_order.read");
  const canManageSos = useCan("sales_order.manage") && canReadSos;
  // B-18: Credit Control overrides a block; for who only reads the orders, they wait on it
  const canOverrideCredit = useCan("credit.manage") && canReadSos;
  // FR-M10-02: Credit Control raises the flag once it sees the payment; for who only reads the orders, they wait on it
  const canConfirmPayment = useCan("payment.manage") && canReadSos;

  const canReadInbounds = useCan("inbound.read");
  const canCheckInbounds = useCan("inbound.check") && canReadInbounds;
  const canFixInbounds = (useCan("inbound.override") || canCheckInbounds) && canReadInbounds;
  // B-04: beyond its tolerance an inbound waits for the brand, which Operations stands for until Atelier (ADR-135)
  const canAuthorizeInbounds = useCan("inbound.authorize") && canReadInbounds;
  // ADR-139: Logistics confirms the outcome of a received inbound with a difference; for who only reads, it waits on it
  const canResolveInbounds = useCan("inbound.resolve") && canReadInbounds;
  // ADR-140: Finance receives or rejects the credit notes of the missing units; for who only reads, it waits on it
  const canReviewCreditNotes = useCan("credit_note.manage") && canReadInbounds;

  const canReadMovements = useCan("movement.read");
  // FR-M12-08: Operations confirms the transfer orders Logistics asked for; for who only reads them, they wait on it
  const canConfirmMovements = useCan("movement.confirm") && canReadMovements;
  // ADR-142: Logistics sets right what an execution of the warehouse lacks and applies it again
  const canRetryExecutions = useCan("movement.request") && canReadMovements;
  // ADR-226: Logistics confirms the adjustments the IMS proposes and applies again a count that waits; Finance approves
  // the ones beyond the threshold; for who only reads them, they wait on the one or the other
  const canConfirmAdjustments = useCan("adjustment.confirm") && canReadMovements;
  const canApproveAdjustments = useCan("adjustment.approve") && canReadMovements;
  // ADR-227, ADR-229: Operations decides a vendor return the supplier refused; who only reads them waits on it, and
  // on the supplier for one it has not answered, whose units stay held
  const canReadVendorReturns = useCan("vendor_return.read");
  const canDecideVendorReturns = useCan("vendor_return.decide") && canReadVendorReturns;
  // ADR-130: Logistics sets right what a shipment of the warehouse lacks and applies it again; for who only reads the
  // fulfillments, it waits on Logistics
  const canReadFulfillments = useCan("fulfillment.read");
  const canRetryShipments = useCan("fulfillment.manage") && canReadFulfillments;
  // FR-M13-02, ADR-152: the Customer Service books by hand what Tomorrow ships; for who only reads, it waits on it
  const canTrackShipments = useCan("shipment.track") && canReadFulfillments;
  // ADR-123, ADR-146: a row of the IMS waits for a mapping or an SKU Logistics sets right, the team that asks the
  // movements from the availability; for who only reads it, it waits on Logistics
  const canReadStock = useCan("stock.read");
  const canResolveAvailability = useCan("movement.request") && canReadStock;
  // B-92, ADR-242: Logistics checks with the IMS what a full publication found different from the events
  const canResolveMismatches = useCan(STOCK_MISMATCH_RESOLVE) && canReadStock;

  const canReadDocuments = useCan("document.read");
  const canRetryDocuments = useCan("document.retry") && canReadDocuments;
  // ADR-178 §1.10, ADR-199: an invoiced shipment the warehouse closed short waits for Finance's credit note
  const canCreditShortShipments = useCan("short_shipment.manage");

  // ADR-105: an e-TAF that was sent or written waits for who reviews it
  const canReadEtafs = useCan("etaf.read");
  const canReviewEtafs = useCan("etaf.review") && canReadEtafs;

  const canReadOrganizations = useCan("organization.read");
  // B-44, B-88, ADR-245: an invitation not accepted after its reminders waits for the Customer Service (customers),
  // which administers their users, or for Operations (brands and suppliers), which follows them up
  const canReadUsers = useCan("user.read");
  const canFollowCustomerInvitations = useCan("user.manage") && canManageSos && canReadUsers;
  const canFollowPartnerInvitations = canManagePos && canReadUsers;
  // B-97, ADR-245: a brand still owing its linesheet after the reminders of its invitation waits for Operations
  const canManageInvitations = useCan("linesheet.manage");
  // B-60, ADR-224: every gate open in any domain waits for the role that decides it
  const canReadGates = useCan("gate.read");
  const canManageOrganizations = useCan("organization.manage") && canReadOrganizations;

  const creditBlocked = turnOrWait(
    {
      id: "sos-credit-blocked",
      place: "sales-orders",
      title: m.queue_sos_credit_blocked(),
      link: salesOrders("sos-credit-blocked"),
    },
    { turn: canOverrideCredit, read: canReadSos, on: m.waiting_on_credit_control() },
  );
  const awaitingPayment = turnOrWait(
    {
      id: "sos-awaiting-payment",
      place: "sales-orders",
      title: m.queue_sos_awaiting_payment(),
      link: salesOrders("sos-awaiting-payment"),
    },
    { turn: canConfirmPayment, read: canReadSos, on: m.waiting_on_credit_control() },
  );
  // ADR-178 §1.12, ADR-203: a payment still missing after the last reminder; Credit Control calls the customer, puts it
  // on hold or cancels the order. For who only reads the orders, they wait on it
  const dunningOver = turnOrWait(
    {
      id: "sos-dunning-over",
      place: "sales-orders",
      title: m.queue_sos_dunning_over(),
      link: salesOrders("sos-dunning-over"),
    },
    { turn: canOverrideCredit, read: canReadSos, on: m.waiting_on_credit_control() },
  );
  // B-47, ADR-208, ADR-221: a confirmation the customer rejected, or left unanswered past its days, waits for the
  // Customer Service, who sends a new revision, cancels the order or calls the customer; for who only reads the orders,
  // they wait on it
  const confirmationsRejected = turnOrWait(
    {
      id: "sos-confirmation-rejected",
      place: "sales-orders",
      title: m.queue_sos_confirmation_rejected(),
      link: salesOrders("sos-confirmation-rejected"),
    },
    { turn: canManageSos, read: canReadSos, on: m.waiting_on_customer_service() },
  );
  const confirmationsOverdue = turnOrWait(
    {
      id: "sos-confirmation-overdue",
      place: "sales-orders",
      title: m.queue_sos_confirmation_overdue(),
      link: salesOrders("sos-confirmation-overdue"),
    },
    { turn: canManageSos, read: canReadSos, on: m.waiting_on_customer_service() },
  );
  const inboundsOutOfTolerance = turnOrWait(
    {
      id: "inbounds-to-authorize",
      place: "inbounds",
      title: m.queue_inbounds_to_authorize(),
      link: inbounds("inbounds-to-authorize"),
    },
    { turn: canAuthorizeInbounds, read: canReadInbounds, on: m.waiting_on_brands() },
  );
  const inboundsToResolve = turnOrWait(
    {
      id: "inbounds-to-resolve",
      place: "inbounds",
      title: m.queue_inbounds_to_resolve(),
      link: inbounds("inbounds-to-resolve"),
    },
    { turn: canResolveInbounds, read: canReadInbounds, on: m.waiting_on_logistics() },
  );
  const creditNotesToReview = turnOrWait(
    {
      id: "credit-notes-to-review",
      place: "credit-notes",
      title: m.queue_credit_notes_to_review(),
      link: creditNotes("credit-notes-to-review"),
    },
    { turn: canReviewCreditNotes, read: canReadInbounds, on: m.waiting_on_finance() },
  );
  const adjustmentsToConfirm = turnOrWait(
    {
      id: "adjustments-to-confirm",
      place: "stock-adjustments",
      title: m.queue_adjustments_to_confirm(),
      link: stockAdjustments("adjustments-to-confirm"),
    },
    { turn: canConfirmAdjustments, read: canReadMovements, on: m.waiting_on_logistics() },
  );
  const vendorReturnsRefused = turnOrWait(
    {
      id: "vendor-returns-refused",
      place: "vendor-returns",
      title: m.queue_vendor_returns_refused(),
      link: vendorReturns("vendor-returns-refused"),
    },
    { turn: canDecideVendorReturns, read: canReadVendorReturns, on: m.waiting_on_operations() },
  );
  const adjustmentsToApprove = turnOrWait(
    {
      id: "adjustments-to-approve",
      place: "stock-adjustments",
      title: m.queue_adjustments_to_approve(),
      link: stockAdjustments("adjustments-to-approve"),
    },
    { turn: canApproveAdjustments, read: canReadMovements, on: m.waiting_on_finance() },
  );
  const transfersToConfirm = turnOrWait(
    {
      id: "transfers-to-confirm",
      place: "stock-movements",
      title: m.queue_transfers_to_confirm(),
      link: stockMovements("transfers-to-confirm"),
    },
    { turn: canConfirmMovements, read: canReadMovements, on: m.waiting_on_operations() },
  );
  const shipmentsWaiting = turnOrWait(
    {
      id: "shipments-waiting",
      place: "warehouse-shipments",
      title: m.queue_shipments_waiting(),
      link: linkOptions({ to: "/warehouse-shipments" }),
    },
    { turn: canRetryShipments, read: canReadFulfillments, on: m.waiting_on_logistics() },
  );
  const fulfillmentsToBook = turnOrWait(
    {
      id: "fulfillments-to-book",
      place: "fulfillments",
      title: m.queue_fulfillments_to_book(),
      link: fulfillments("fulfillments-to-book"),
    },
    { turn: canTrackShipments, read: canReadFulfillments, on: m.waiting_on_customer_service() },
  );
  // FR-M13-06, ADR-152: Logistics checks with the warehouse a release it has not taken, and sends it again if needed
  const fulfillmentsWithoutAck = turnOrWait(
    {
      id: "fulfillments-without-ack",
      place: "fulfillments",
      title: m.queue_fulfillments_without_ack(),
      link: fulfillments("fulfillments-without-ack"),
    },
    { turn: canRetryShipments, read: canReadFulfillments, on: m.waiting_on_logistics() },
  );
  const availabilityWaiting = turnOrWait(
    {
      id: "availability-waiting",
      place: "availability-waiting",
      title: m.queue_availability_waiting(),
      link: linkOptions({ to: "/availability-waiting" }),
    },
    { turn: canResolveAvailability, read: canReadStock, on: m.waiting_on_logistics() },
  );
  const availabilityMismatches = turnOrWait(
    {
      id: "availability-mismatches",
      place: "availability-mismatches",
      title: m.queue_availability_mismatches(),
      link: linkOptions({ to: "/availability-mismatches" }),
    },
    { turn: canResolveMismatches, read: canReadStock, on: m.waiting_on_logistics() },
  );
  // B-37, B-41, B-42, ADR-245: what the supplier left undone after its last reminder and a cadence waits for who
  // owns the process: Operations chases a PO, Logistics the missing and the extra units, Finance a credit note
  const posRemindersOver = turnOrWait(
    {
      id: "pos-reminders-over",
      place: "purchase-orders",
      title: m.queue_pos_reminders_over(),
      link: purchaseOrders("pos-reminders-over"),
    },
    { turn: canManagePos, read: canReadPos, on: m.waiting_on_operations() },
  );
  const inboundsRemindersOver = turnOrWait(
    {
      id: "inbounds-reminders-over",
      place: "inbounds",
      title: m.queue_inbounds_reminders_over(),
      link: inbounds("inbounds-reminders-over"),
    },
    { turn: canResolveInbounds, read: canReadInbounds, on: m.waiting_on_logistics() },
  );
  const creditNotesRemindersOver = turnOrWait(
    {
      id: "credit-notes-reminders-over",
      place: "credit-notes",
      title: m.queue_credit_notes_reminders_over(),
      link: creditNotes("credit-notes-reminders-over"),
    },
    { turn: canReviewCreditNotes, read: canReadInbounds, on: m.waiting_on_finance() },
  );
  const customerInvitations = turnOrWait(
    {
      id: "invitations-reminders-over-customers",
      place: "users",
      title: m.queue_invitations_reminders_over_customers(),
      link: users("invitations-reminders-over-customers"),
    },
    { turn: canFollowCustomerInvitations, read: canReadUsers, on: m.waiting_on_customer_service() },
  );
  const partnerInvitations = turnOrWait(
    {
      id: "invitations-reminders-over-partners",
      place: "users",
      title: m.queue_invitations_reminders_over_partners(),
      link: users("invitations-reminders-over-partners"),
    },
    { turn: canFollowPartnerInvitations, read: canReadUsers, on: m.waiting_on_operations() },
  );
  const documentsToAskAgain = turnOrWait(
    {
      id: "documents-to-retry",
      place: "documents",
      title: m.queue_documents_to_retry(),
      link: documents("documents-to-retry"),
    },
    { turn: canRetryDocuments, read: canReadDocuments, on: m.waiting_on_customer_service() },
  );

  const yourTurn: (Shown | false)[] = [
    canApprove && {
      id: "to-approve",
      place: "linesheets",
      title: m.queue_linesheets_to_approve(),
      link: linesheets("to-approve"),
    },
    canPrice && {
      id: "to-price",
      place: "linesheets",
      title: m.queue_prices_to_complete(),
      link: linesheets("to-price"),
    },
    canPrice && {
      id: "to-recalculate",
      place: "linesheets",
      title: m.prices_to_recalculate(),
      link: linesheets("to-recalculate"),
    },
    canReadIntegration && {
      id: "integration-errors",
      place: "integration",
      title: m.nav_integration(),
      link: linkOptions({ to: "/admin/integration" }),
    },
    canReadIntegration && {
      id: "bc-mismatches",
      place: "reconciliation",
      title: m.queue_bc_mismatches(),
      link: linkOptions({ to: "/admin/reconciliation" }),
    },
    canReadErpFiles && {
      id: "erp-files-to-import",
      place: "erp-files",
      title: m.queue_erp_files_to_import(),
      link: linkOptions({ to: "/erp-files" }),
    },
    canManageAttributes && {
      id: "attributes-to-review",
      place: "attributes",
      title: m.queue_attributes_to_review(),
      link: attributeTasks("attributes-to-review"),
    },
    canApprovePos && {
      id: "pos-to-approve",
      place: "purchase-orders",
      title: m.queue_pos_to_approve(),
      link: purchaseOrders("pos-to-approve"),
    },
    canManagePos && {
      id: "pos-to-publish",
      place: "purchase-orders",
      title: m.queue_pos_to_publish(),
      link: purchaseOrders("pos-to-publish"),
    },
    canManagePos && {
      id: "pos-to-decide",
      place: "purchase-orders",
      title: m.queue_pos_to_decide(),
      link: purchaseOrders("pos-to-decide"),
    },
    canManagePos && {
      id: "pos-sync-failed",
      place: "purchase-orders",
      title: m.queue_pos_sync_failed(),
      link: purchaseOrders("pos-sync-failed"),
    },
    posRemindersOver.turn,
    canManageInvitations && {
      id: "linesheet-invitations-reminders-over",
      place: "invitations",
      title: m.queue_linesheet_invitations_reminders_over(),
      link: linkOptions({ to: "/linesheet-invitations", search: { remindersOver: true } }),
    },
    canManageSos && {
      id: "sos-to-allocate",
      place: "sales-orders",
      title: m.queue_sos_to_allocate(),
      link: salesOrders("sos-to-allocate"),
    },
    canManageSos && {
      id: "sos-to-lock",
      place: "sales-orders",
      title: m.queue_sos_to_lock(),
      link: salesOrders("sos-to-lock"),
    },
    canManageSos && {
      id: "sos-sync-failed",
      place: "sales-orders",
      title: m.queue_sos_sync_failed(),
      link: salesOrders("sos-sync-failed"),
    },
    canManageSos && {
      id: "sos-fulfillment-stopped",
      place: "sales-orders",
      title: m.queue_sos_fulfillment_stopped(),
      link: salesOrders("sos-fulfillment-stopped"),
    },
    canManageSos && {
      id: "sos-uncovered",
      place: "sales-orders",
      title: m.queue_sos_uncovered(),
      link: salesOrders("sos-uncovered"),
    },
    // C-56, ADR-259: the allocation ranks an order without a season as of no active season
    canManageSos && {
      id: "sos-without-season",
      place: "sales-orders",
      title: m.queue_sos_without_season(),
      link: salesOrders("sos-without-season"),
    },
    // C-45, ADR-261: an order no confirmation was issued or asked for waits for the Customer Service, who issues it
    canManageSos && {
      id: "sos-without-confirmation",
      place: "sales-orders",
      title: m.queue_sos_without_confirmation(),
      link: salesOrders("sos-without-confirmation"),
    },
    confirmationsRejected.turn,
    confirmationsOverdue.turn,
    creditBlocked.turn,
    canCreditShortShipments && {
      id: "short-shipments-to-credit",
      place: "short-shipments",
      title: m.queue_short_shipments_to_credit(),
      link: linkOptions({ to: "/short-shipments", search: filtersOf("short-shipments-to-credit") }),
    },
    canManageSos && {
      id: "joor-orders-waiting",
      place: "joor-orders",
      title: m.queue_joor_orders_waiting(),
      link: linkOptions({ to: "/joor-orders" }),
    },
    awaitingPayment.turn,
    canConfirmPayment && {
      id: "sos-deposit-due",
      place: "sales-orders",
      title: m.queue_sos_deposit_due(),
      link: salesOrders("sos-deposit-due"),
    },
    dunningOver.turn,
    documentsToAskAgain.turn,
    canCheckInbounds && {
      id: "inbounds-to-capture",
      place: "inbounds",
      title: m.queue_inbounds_to_capture(),
      link: inbounds("inbounds-to-capture"),
    },
    // ADR-233: lines the extraction tool read and was not sure of, or with an invoice that does not add up
    canCheckInbounds && {
      id: "inbounds-extraction-to-review",
      place: "inbounds",
      title: m.queue_inbounds_extraction_to_review(),
      link: inbounds("inbounds-extraction-to-review"),
    },
    canCheckInbounds && {
      id: "inbounds-to-check",
      place: "inbounds",
      title: m.queue_inbounds_to_check(),
      link: inbounds("inbounds-to-check"),
    },
    canFixInbounds && {
      id: "inbounds-in-exception",
      place: "inbounds",
      title: m.queue_inbounds_in_exception(),
      link: inbounds("inbounds-in-exception"),
    },
    canCheckInbounds && {
      id: "inbounds-to-dispatch",
      place: "inbounds",
      title: m.queue_inbounds_to_dispatch(),
      link: inbounds("inbounds-to-dispatch"),
    },
    // I-11 §5, ADR-138: a receipt of the warehouse Nexxt could not apply waits for Logistics
    canCheckInbounds && {
      id: "receipts-waiting",
      place: "warehouse-receipts",
      title: m.queue_receipts_waiting(),
      link: linkOptions({ to: "/warehouse-receipts" }),
    },
    inboundsOutOfTolerance.turn,
    inboundsToResolve.turn,
    inboundsRemindersOver.turn,
    creditNotesToReview.turn,
    creditNotesRemindersOver.turn,
    customerInvitations.turn,
    partnerInvitations.turn,
    transfersToConfirm.turn,
    adjustmentsToConfirm.turn,
    adjustmentsToApprove.turn,
    vendorReturnsRefused.turn,
    // I-11 §5, ADR-226: a count of the warehouse Nexxt could not make an adjustment of waits for Logistics
    canConfirmAdjustments && {
      id: "differences-waiting",
      place: "warehouse-counts",
      title: m.queue_differences_waiting(),
      link: linkOptions({ to: "/warehouse-counts" }),
    },
    // I-11 §5, ADR-142: an execution of the warehouse Nexxt could not apply waits for Logistics
    canRetryExecutions && {
      id: "movement-executions-waiting",
      place: "warehouse-executions",
      title: m.queue_movement_executions_waiting(),
      link: linkOptions({ to: "/warehouse-executions" }),
    },
    shipmentsWaiting.turn,
    fulfillmentsToBook.turn,
    fulfillmentsWithoutAck.turn,
    availabilityWaiting.turn,
    availabilityMismatches.turn,
    canReviewEtafs && {
      id: "etafs-to-review",
      place: "etaf",
      title: m.queue_etafs_to_review(),
      link: linkOptions({ to: "/etaf" }),
    },
    canManageOrganizations && {
      id: "organizations-not-on-bc",
      place: "organizations",
      title: m.queue_organizations_sync_failed(),
      link: linkOptions({ to: "/organizations", search: filtersOf("organizations-not-on-bc") }),
    },
    // ADR-093, ADR-189: a supplier whose open POs are not confirmed until someone registers it on Business Central
    canManageOrganizations && {
      id: "suppliers-not-on-bc",
      place: "organizations",
      title: m.queue_suppliers_not_on_bc(),
      link: linkOptions({ to: "/organizations", search: filtersOf("suppliers-not-on-bc") }),
    },
    // ADR-158, ADR-180: Customer Service gives a contact of the customer the role that receives the order emails
    canManageOrganizations && {
      id: "customers-without-order-contact",
      place: "organizations",
      title: m.queue_customers_without_order_contact(),
      link: linkOptions({ to: "/organizations", search: filtersOf("customers-without-order-contact") }),
    },
    canManageOrganizations && {
      id: "bc-records-to-match",
      place: "bc-records",
      title: m.queue_bc_records_to_match(),
      link: linkOptions({ to: "/bc-records" }),
    },
    // ADR-178 §4.4, ADR-191: a full reading of HubSpot that would archive too much, or lost parts, archives nothing
    // until someone decides
    canManageOrganizations && {
      id: "hubspot-readings-held",
      place: "hubspot-records",
      title: m.queue_hubspot_readings_held(),
      link: linkOptions({ to: "/hubspot-records" }),
    },
  ];
  const waiting: (Shown | false)[] = [
    canRead && {
      id: "in-draft",
      place: "linesheets",
      title: m.queue_linesheets_in_draft(),
      on: m.waiting_on_brands(),
      link: linesheets("in-draft"),
    },
    canReadAttributes && {
      id: "attributes-to-fill",
      place: "attributes",
      title: m.queue_attributes_to_fill(),
      on: m.waiting_on_brands(),
      link: attributeTasks("attributes-to-fill"),
    },
    canReadPos && {
      id: "pos-to-confirm",
      place: "purchase-orders",
      title: m.queue_pos_to_confirm(),
      on: m.waiting_on_suppliers(),
      link: purchaseOrders("pos-to-confirm"),
    },
    posRemindersOver.wait,
    confirmationsRejected.wait,
    confirmationsOverdue.wait,
    creditBlocked.wait,
    awaitingPayment.wait,
    dunningOver.wait,
    documentsToAskAgain.wait,
    canReadDocuments && {
      id: "documents-pdf-late",
      place: "documents",
      title: m.queue_documents_pdf_late(),
      on: m.waiting_on_document_service(),
      link: documents("documents-pdf-late"),
    },
    inboundsOutOfTolerance.wait,
    inboundsToResolve.wait,
    // ADR-140: the supplier chooses for the missing units, then sends the file of its credit note
    canReadInbounds && {
      id: "inbounds-to-settle",
      place: "inbounds",
      title: m.queue_inbounds_to_settle(),
      on: m.waiting_on_suppliers(),
      link: inbounds("inbounds-to-settle"),
    },
    canReadInbounds && {
      id: "credit-notes-to-send",
      place: "credit-notes",
      title: m.queue_credit_notes_to_send(),
      on: m.waiting_on_suppliers(),
      link: creditNotes("credit-notes-to-send"),
    },
    creditNotesToReview.wait,
    inboundsRemindersOver.wait,
    creditNotesRemindersOver.wait,
    customerInvitations.wait,
    partnerInvitations.wait,
    transfersToConfirm.wait,
    adjustmentsToConfirm.wait,
    adjustmentsToApprove.wait,
    vendorReturnsRefused.wait,
    canReadVendorReturns && {
      id: "vendor-returns-to-confirm",
      place: "vendor-returns",
      title: m.queue_vendor_returns_to_confirm(),
      on: m.waiting_on_suppliers(),
      link: vendorReturns("vendor-returns-to-confirm"),
    },
    shipmentsWaiting.wait,
    fulfillmentsToBook.wait,
    fulfillmentsWithoutAck.wait,
    availabilityWaiting.wait,
    availabilityMismatches.wait,
    canReadOrganizations && {
      id: "hubspot-records-waiting",
      place: "hubspot-records",
      title: m.queue_hubspot_records_waiting(),
      on: m.waiting_on_hubspot(),
      link: linkOptions({ to: "/hubspot-records" }),
    },
    canReadGates && {
      id: "approvals-waiting",
      place: "approvals",
      title: m.queue_approvals_waiting(),
      on: m.waiting_on_approvers(),
      link: linkOptions({ to: "/admin/approvals" }),
    },
  ];
  const queues = useWorkQueues(yourTurn, waiting, homeQueries.counts, unionsOf);
  return { ...queues, todo: todosOf(queues.yourTurn, queues.unions) };
}

/** The "your turn" queues of each place with the filter "To do", in the order of the home. */
function byTodoPlace<Q extends Shown>(mine: Q[]): Map<string, Q[]> {
  const places = new Map<string, Q[]>();
  for (const queue of mine)
    if (todoLinks[queue.place]) places.set(queue.place, [...(places.get(queue.place) ?? []), queue]);
  // a union joins the queues of one list: a place that mixes two would make worklist refuse every count
  for (const [place, queues] of places)
    if (new Set(queues.map((q) => workQueues[q.id].count)).size > 1) places.delete(place);
  return places;
}

/** The unions worklist counts (ADR-302): a place with one queue has its count already. */
const unionsOf = (mine: Shown[]): QueueId[][] =>
  [...byTodoPlace(mine).values()].filter((queues) => queues.length > 1).map((queues) => queues.map((q) => q.id));

function todosOf(mine: Queue[], unions: { ids: QueueId[]; data: ListCount | null | undefined }[]) {
  const todos: Record<string, Todo> = {};
  for (const [place, queues] of byTodoPlace(mine)) {
    const ids = queues.map((q) => q.id).join(".");
    const union = unions.find((u) => u.ids.join(".") === ids);
    todos[place] = {
      queues,
      count: queues.length === 1 ? queues[0]?.data?.count : (union?.data?.count ?? undefined),
      anyOf: JSON.stringify(queues.map((q) => filtersOf(q.id) ?? {})),
      link: todoLinks[place] as LinkOptions,
    };
  }
  return todos;
}

/** The filter "To do" of a place for the user: null when none of its queues is the user's turn. */
function useTodo(place: string): Todo | null {
  return useQueues().todo[place] ?? null;
}

/** What a list of a place needs for its filter "To do" (ADR-302). */
export interface TodoFilterOf {
  /** The sets of the queues the list sends as `anyOf` while the filter is on. */
  anyOf: string | undefined;
  /** The filter, first among the filters of the list: null when none of the queues is the user's turn. */
  filter: ReactNode;
  /** While the filter is on, the titles of the queues in the order of `anyOf`: the reasons of the rows. */
  titles: string[] | undefined;
}

/**
 * The filter "To do" of the list of a place: `active` is the `todo` of the search of the list, `onChange` writes it
 * there. It is on only while one of the queues of the place is the user's turn.
 */
export function useTodoFilter(
  place: string,
  active: boolean | undefined,
  onChange: (todo: true | undefined) => void,
): TodoFilterOf {
  const todo = useTodo(place);
  const on = active === true && todo !== null;
  return {
    anyOf: on ? todo.anyOf : undefined,
    filter: todo && createElement(TodoFilter, { count: todo.count, active: on, onChange }),
    titles: on ? todo.queues.map((q) => q.title) : undefined,
  };
}
